/**
 * Web-only styles for View Requests `createPortal` filter/sort dropdowns (#1047).
 * Uses existing `src/theme` exports on `development` — no parallel token modules.
 */
import type { CSSProperties } from 'react';
import { colors, fontSize, fontWeight, layout, letterSpacing, spacing } from '../theme';
import { elevation } from '../theme/elevation';

const vr = layout.viewRequests;

const borderSolid = (color: string): string => `1px solid ${color}`;

function portalDropdownBoxShadow(): string {
  const webPopover = elevation.popover as CSSProperties;
  return (webPopover.boxShadow as string | undefined) ?? '0 4px 12px rgba(0, 0, 0, 0.25)';
}

export function getDropdownPortalRootStyle(pos: {
  top: number;
  left: number;
  width: number;
}): CSSProperties {
  return {
    position: 'absolute',
    top: pos.top,
    left: pos.left,
    width: pos.width,
    backgroundColor: colors.backgroundPrimary,
    border: borderSolid(colors.borderMedium),
    borderRadius: `${vr.dropdownBorderRadius}px`,
    maxHeight: `${vr.dropdownMaxHeight}px`,
    boxShadow: portalDropdownBoxShadow(),
    zIndex: vr.zIndexPortal,
    overflow: 'hidden',
  };
}

export const dropdownPortalHeaderRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${spacing.md}px ${spacing.lg}px`,
  borderBottom: borderSolid(colors.borderLight),
};

export const dropdownPortalTitle: CSSProperties = {
  fontSize: `${fontSize.subheading}px`,
  fontWeight: fontWeight.semibold,
  color: colors.textPrimary,
};

export const dropdownPortalHeaderButton: CSSProperties = {
  background: 'none',
  border: 'none',
  color: colors.primary,
  fontSize: `${fontSize.formControl}px`,
  fontWeight: fontWeight.medium,
  cursor: 'pointer',
};

export const dropdownPortalScrollArea: CSSProperties = {
  padding: `${spacing.sm}px 0`,
  maxHeight: `${vr.dropdownScrollMaxHeight}px`,
  overflowY: 'auto',
};

export const filterSectionPadding: CSSProperties = {
  padding: `${spacing.sm}px ${spacing.lg}px`,
};

export const filterSectionHeaderRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: `${spacing.sm}px`,
};

export const filterSectionLabel: CSSProperties = {
  fontSize: `${fontSize.charCount}px`,
  fontWeight: fontWeight.semibold,
  color: colors.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: `${letterSpacing.fieldLabelUppercase}px`,
};

export const filterSectionClearButton: CSSProperties = {
  background: 'none',
  border: 'none',
  color: colors.primary,
  fontSize: `${fontSize.charCount}px`,
  fontWeight: fontWeight.medium,
  cursor: 'pointer',
  textTransform: 'uppercase',
  letterSpacing: `${letterSpacing.fieldLabelUppercase}px`,
};

export function getFilterOptionRowStyle(isSelected: boolean): CSSProperties {
  return {
    padding: `${spacing.sm}px ${spacing.md}px`,
    borderRadius: `${Math.max(4, vr.dropdownBorderRadius - 2)}px`,
    cursor: 'pointer',
    backgroundColor: isSelected ? colors.surfaceAccentMuted : 'transparent',
    color: isSelected ? colors.primary : colors.textPrimary,
    fontWeight: isSelected ? fontWeight.medium : 'normal',
  };
}

export function getSortOptionRowStyle(isSelected: boolean): CSSProperties {
  return {
    padding: `${spacing.sm}px ${spacing.lg}px`,
    cursor: 'pointer',
    backgroundColor: isSelected ? colors.surfaceAccentMuted : 'transparent',
    color: isSelected ? colors.primary : colors.textPrimary,
    fontWeight: isSelected ? fontWeight.medium : 'normal',
  };
}

export const sortDirectionHint: CSSProperties = {
  fontSize: `${fontSize.charCount}px`,
  color: colors.textSecondary,
  fontWeight: '400',
};
